import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Container, Grid, Typography, Button, ThemeProvider, createTheme, Card, CardContent, CardActionArea, Slide, IconButton, Divider, Tooltip as MuiTooltip, List, ListItem, ListItemIcon, ListItemText, Dialog, DialogContent, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CountUp from 'react-countup';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title } from 'chart.js';
import ROICalculator from './components/ROICalculator';
import ResultsDisplay from './components/ResultsDisplay';
import ContactDialog from './components/ContactDialog';
import AnimatedBackground from './components/AnimatedBackground';
import { 
  ProcessAutomationSVG, 
  DocumentProcessingSVG, 
  DashboardSVG, 
  AppDevelopmentSVG, 
  ConsultingSVG, 
  AISVG,
  GlassCard 
} from './components/TechIcons';
import SopraLogo from './assets/sopra_steria.png';
import AFLogo from './assets/AF_logo.png';
import UiPathLogo from './assets/uipath-vector-logo.png';
import PythonLogo from './assets/python_logo_icon_168886.jpg';
import PowerShellLogo from './assets/powershell.webp';
import PowerPlatformLogo from './assets/power-platform.png';
import AILogo from './assets/ai.jpg';
import EuropcarLogo from './assets/brand/Europcar-Logo.png';
import LaPosteLogo from './assets/brand/Laposte.png';
import CevaLogo from './assets/brand/ceva-logo.png';
import UiPathPartnerLogo from './assets/uipath_partner.png';
import MSPartnerLogo from './assets/ms_partner.png';
import { generateROIPDF } from './utils/pdfGenerator';
import CompanyTooltip from './components/CompanyTooltip';

// Create a modern theme
const theme = createTheme({
  typography: {
    fontFamily: '"Outfit", "Sora", "Roboto", sans-serif',
    h1: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    h2: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Sora", sans-serif',
      fontWeight: 600,
    },
    body1: {
      fontFamily: '"Sora", sans-serif',
    },
    body2: {
      fontFamily: '"Sora", sans-serif',
    },
  },
  palette: {
    primary: {
      main: '#db1d2b',
      light: '#ff4d5a',
      dark: '#a01420',
      contrastText: '#fff'
    },
    secondary: {
      main: '#f47216',
      light: '#ff9344',
      dark: '#c45a0f',
      contrastText: '#fff'
    },
    text: {
      primary: '#231f20',
      secondary: '#5a6775',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          color: '#f47216',
          height: 8,
          '& .MuiSlider-track': {
            border: 'none',
            background: 'linear-gradient(90deg, #f47216 0%, #db1d2b 100%)',
          },
          '& .MuiSlider-rail': {
            background: 'rgba(0, 0, 0, 0.1)',
          },
          '& .MuiSlider-thumb': {
            height: 24,
            width: 24,
            backgroundColor: '#fff',
            border: '3px solid #f47216',
            boxShadow: '0 2px 10px rgba(244, 114, 22, 0.3)',
            '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
              boxShadow: '0 4px 20px rgba(244, 114, 22, 0.4)',
            },
          },
        },
      },
    },
  },
});

// Register ChartJS components
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement,
  BarElement,
  Title
);

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const fadeInScale = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

function App() {
  const [calculationData, setCalculationData] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedStory, setSelectedStory] = useState(null);
  const [animateStats, setAnimateStats] = useState(false);
  const calculatorRef = useRef(null);
  const servicesRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimateStats(true);
        }
      },
      { threshold: 0.1 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);

  const handleCalculate = useCallback((data) => {
    setCalculationData(data);
  }, []);

  const handleOpenContactDialog = () => setContactDialogOpen(true);
  const handleCloseContactDialog = () => setContactDialogOpen(false);

  const handleGenerateReport = () => {
    if (calculationData) {
      const pdfDoc = generateROIPDF(calculationData, calculationData);
      const pdfData = pdfDoc.output('datauristring');
      const link = document.createElement('a');
      link.href = pdfData;
      link.download = 'Automation_Factory_ROI_Analysis_Report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const scrollToCalculator = () => calculatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  const scrollToServices = () => servicesRef.current?.scrollIntoView({ behavior: 'smooth' });
  const handleServiceClick = (service) => setSelectedService(service);
  const handleCloseServiceDetails = () => setSelectedService(null);
  const handleOpenStory = (story) => setSelectedStory(story);
  const handleCloseStory = () => setSelectedStory(null);

  // Service items with SVG icons
  const serviceItems = [
    {
      title: "Consulting",
      description: "Expert guidance to kickstart your process automation journey, from discovery to strategy.",
      IconComponent: ConsultingSVG,
      fullDescription: "Our Consulting services help your company successfully start its process automation journey with expert support during the critical discovery phase.",
      benefits: ["Identify best-fit processes for automation", "Develop a clear automation strategy and roadmap", "Select appropriate automation technologies", "Build internal CoE capabilities"],
      metrics: {},
      technologies: [],
      useCase: "Engaged with a mid-sized logistics company to assess operations for automation potential and delivered a prioritized roadmap.",
      why: "Our consulting approach combined technical expertise with business process understanding.",
      caseStudies: [
        {
          title: "Automation Audit & Optimization",
          description: "Performed comprehensive audit of existing automation portfolio, improving overall ROI by 25%.",
          fullStory: {
            client: "Pan-European Insurance Group",
            industry: "Banking & Insurance",
            challenge: "The company had built 35 automations over 4 years with different vendors and approaches. Many bots were running below design capacity, poorly documented, and costly to maintain. Leadership suspected ROI was well below potential but lacked visibility to act.",
            solution: "Our consultants conducted a full portfolio audit — interviewing stakeholders, reviewing bot run logs, analysing exception rates, and benchmarking performance against industry standards. We delivered a prioritised optimisation roadmap identifying quick wins (bot tuning), medium-term fixes (redesigns), and retirement candidates.",
            results: ["Portfolio ROI improved by 25% within 6 months of implementing recommendations", "7 underperforming bots redesigned — exception rates dropped from 18% to 3%", "4 redundant bots retired, saving €45k/year in licensing", "Governance framework established preventing future portfolio degradation"],
            metrics: [{ label: "ROI Improvement", value: "+25%" }, { label: "Exception Rate", value: "-83%" }, { label: "Cost Savings", value: "€45k/yr" }, { label: "Bots Audited", value: "35" }],
            technologies: []
          }
        },
        {
          title: "Process Discovery Workshop",
          description: "Facilitated process discovery initiative identifying over 50 potential automation candidates.",
          fullStory: {
            client: "Mid-Size Logistics Company — 850 Employees",
            industry: "Transportation & Logistics",
            challenge: "The company wanted to launch an automation programme but had no structured way to identify which processes to prioritise. Previous ad-hoc attempts had targeted the wrong processes — delivering poor ROI and eroding stakeholder trust in automation.",
            solution: "We ran a structured 3-week Process Discovery engagement: workshops across 6 departments, task mining sessions with process recording tools, and ROI modelling for each candidate. We applied our proprietary prioritisation matrix scoring automation potential, complexity, business impact, and data sensitivity.",
            results: ["52 automation candidates identified and scored across 6 departments", "Top 10 candidates projected to deliver €420k annual savings combined", "Executive stakeholder buy-in secured with a compelling business case", "Automation Centre of Excellence structure defined and resourced"],
            metrics: [{ label: "Candidates Found", value: "52" }, { label: "Projected Savings", value: "€420k" }, { label: "Departments", value: "6" }, { label: "CoE", value: "Launched" }],
            technologies: []
          }
        },
        {
          title: "Open-Source Migration Strategy",
          description: "Developed strategic plan for migrating automation processes to open-source tools.",
          fullStory: {
            client: "Public Sector Agency",
            industry: "Public Sector",
            challenge: "Rising licensing costs for proprietary RPA tools had become unsustainable — the agency faced a 40% price increase at renewal. Leadership needed a credible migration path to open-source alternatives without disrupting 28 production automations serving critical public services.",
            solution: "We conducted a detailed technology assessment comparing open-source alternatives against the current stack. For each of the 28 bots, we mapped migration complexity, estimated effort, and recommended tooling. We delivered a phased migration roadmap, reusable component library designs, and a pilot migration of 5 bots to validate the approach.",
            results: ["€180k/year in licensing costs eliminated post-migration", "28 bots successfully migrated over 9 months with zero production disruptions", "Pilot phase validated 90-day migration methodology", "Internal team upskilled — no ongoing vendor dependency"],
            metrics: [{ label: "Savings", value: "€180k/yr" }, { label: "Bots Migrated", value: "28" }, { label: "Downtime", value: "Zero" }, { label: "Timeline", value: "9 months" }],
            technologies: ["Python"]
          }
        }
      ]
    },
    {
      title: "Process Automation",
      description: "Automate repetitive manual tasks to free up staff for higher value work and improve accuracy.",
      IconComponent: ProcessAutomationSVG,
      fullDescription: "Our Process Automation solutions utilize RPA to handle repetitive, rule-based tasks across applications and systems. By mimicking human interactions with digital systems, we can automate workflows that previously required manual intervention.",
      benefits: ["Reduce processing time by up to 80%", "Minimize human error in repetitive tasks", "Operate 24/7 without interruption", "Scale quickly to handle volume spikes"],
      metrics: { ftes: "3.5", profit: "€110,350", roi: "142%", actions: "1,800", accuracy: "97%", employeeCost: "€185" },
      technologies: ['UiPath', 'PowerShell'],
      useCase: "An automated IT system monitoring process that detects problems through defined alarm thresholds and automatically creates tickets in service management systems.",
      why: "Our automation solution eliminated human error in repetitive checks, provided consistent 24/7 monitoring, and enabled proactive issue detection.",
      caseStudies: [
        {
          title: "Automated Daily System Checks",
          description: "Implemented automation for daily sanity checks across critical IT infrastructure, reducing manual verification time by 95%.",
          fullStory: {
            client: "Large Financial Services Firm",
            industry: "Banking & Insurance",
            challenge: "The IT operations team spent 3 hours every morning manually checking 40+ servers, databases, and services across multiple environments. This repetitive work was error-prone and delayed real issue detection, often resulting in missed SLA breaches.",
            solution: "We deployed a UiPath RPA bot that runs automatically every morning at 6:00 AM. It connects to each system, runs predefined health checks, parses logs, compares results against thresholds, and generates a consolidated HTML status report — all before the team arrives at the office. Critical failures trigger immediate alerts via Teams and email.",
            results: ["95% reduction in manual verification time — from 3 hours to 9 minutes", "Zero missed checks since deployment (previously ~3-4 missed per month)", "Alerts now dispatched in under 2 minutes vs. 30+ minutes previously", "Team reallocated 15 hours/week to proactive infrastructure improvements"],
            metrics: [{ label: "Time Saved", value: "95%" }, { label: "Checks / Day", value: "40+" }, { label: "Alert Speed", value: "-28 min" }, { label: "ROI", value: "312%" }],
            technologies: ["UiPath", "PowerShell"]
          }
        },
        {
          title: "Incident Ticket Acknowledgement",
          description: "Automated the acknowledgement process for incoming support tickets, improving response times by 60%.",
          fullStory: {
            client: "Telecom Operator — IT Service Desk",
            industry: "Telecommunications",
            challenge: "The service desk was receiving 300–500 tickets daily. Manual acknowledgement meant tickets often sat unresponded for 45–90 minutes, causing SLA violations and customer complaints. Agents were spending ~2 hours per day solely on acknowledgements.",
            solution: "A UiPath bot monitors the ticketing queue continuously. Upon detecting a new ticket, it validates the category, assigns the correct team queue, sends a personalised acknowledgement email to the requester, and updates the ticket status — all within seconds of ticket creation.",
            results: ["60% improvement in average first response time", "SLA compliance improved from 74% to 97%", "Agents saved ~2 hours/day, redirected to complex resolutions", "Customer satisfaction scores up 22 points"],
            metrics: [{ label: "Response Time", value: "-60%" }, { label: "SLA Rate", value: "97%" }, { label: "Daily Tickets", value: "500+" }, { label: "CSAT Gain", value: "+22pts" }],
            technologies: ["UiPath"]
          }
        },
        {
          title: "Automated Server Management",
          description: "Utilized process automation to manage routine server maintenance tasks, reducing manual effort by 80%.",
          fullStory: {
            client: "European Logistics Company",
            industry: "Transportation & Logistics",
            challenge: "System administrators performed 12+ routine server tasks weekly — log cleanup, disk space checks, patch status reviews, and service restarts. This consumed ~20 hours/week of senior admin time and was frequently deprioritised, leading to disk overflows and unplanned outages.",
            solution: "We built a PowerShell-based automation suite orchestrated by UiPath, scheduled across a weekly maintenance window. Tasks include automated log archiving, disk space remediation, stale process termination, and patch compliance reporting delivered to management dashboards.",
            results: ["80% reduction in manual server management effort", "3 disk overflow incidents eliminated in first 6 months", "Weekly patch compliance report now fully automated", "Senior engineers freed to focus on architecture and security initiatives"],
            metrics: [{ label: "Manual Effort", value: "-80%" }, { label: "Hours/Week Saved", value: "16h" }, { label: "Incidents Avoided", value: "3/mo" }, { label: "ROI", value: "290%" }],
            technologies: ["UiPath", "PowerShell"]
          }
        }
      ]
    },
    {
      title: "Document Processing",
      description: "Extract data from documents, classify information, and route for processing efficiently.",
      IconComponent: DocumentProcessingSVG,
      fullDescription: "Our Document Processing solutions combine OCR, machine learning, and RPA to automate the extraction, processing, and routing of information from both structured and unstructured documents.",
      benefits: ["Process thousands of documents in minutes", "Extract data with high accuracy", "Automatically classify and route information", "Integrate with existing document management systems"],
      metrics: { ftes: "5.2", profit: "€164,940", roi: "353%", actions: "1,600", accuracy: "94%", employeeCost: "€185" },
      technologies: ['UiPath', 'AI'],
      useCase: "An automated digitization process for accounting documents using OCR technology for data extraction and automatic entry into accounting systems.",
      why: "Our document processing solution combined advanced OCR with AI-powered validation, ensuring high accuracy while handling various document formats.",
      caseStudies: [
        {
          title: "Automated Report Reformatting",
          description: "A consulting firm automated reformatting internal reports into customer-ready presentations, reducing manual design work by 70%.",
          fullStory: {
            client: "Mid-size Management Consulting Firm",
            industry: "Professional Services",
            challenge: "Consultants spent up to 4 hours per engagement manually converting internal analysis reports into polished, brand-compliant PowerPoint presentations for clients. This bottleneck delayed deliverables and diverted senior consultant time away from analysis.",
            solution: "We developed a UiPath + Python solution that reads structured internal report templates, extracts key data sections, and dynamically populates branded PowerPoint slides via the python-pptx library. The bot handles charts, tables, and narrative sections, generating a client-ready deck in under 3 minutes.",
            results: ["70% reduction in manual presentation design time", "Deliverable turnaround improved from 2 days to 4 hours", "Brand compliance rate reached 100% — zero manual formatting corrections", "Consultants reclaimed 8+ hours per week for billable analysis work"],
            metrics: [{ label: "Design Time", value: "-70%" }, { label: "Turnaround", value: "-75%" }, { label: "Brand Compliance", value: "100%" }, { label: "Hours Saved/Week", value: "8h" }],
            technologies: ["UiPath", "Python"]
          }
        },
        {
          title: "Intelligent Document Sorting",
          description: "Implemented AI-powered solution to automatically categorize incoming emails and attachments, reducing sorting time by 85%.",
          fullStory: {
            client: "Insurance Claims Processing Centre",
            industry: "Banking & Insurance",
            challenge: "The claims team received 800+ emails and attachments daily across a shared inbox — invoices, medical certificates, photos, legal documents, and customer queries. Manual sorting consumed 3 FTEs and classification errors caused processing delays of up to 48 hours.",
            solution: "We built an AI classification pipeline using a fine-tuned transformer model to categorize each email and attachment by document type and urgency. The UiPath bot then routes documents to the correct SharePoint folder and downstream processing queue automatically, with confidence scores logged for audit purposes.",
            results: ["85% reduction in manual document sorting time", "Classification accuracy of 94% across 12 document categories", "Processing delays cut from 48 hours to under 2 hours", "3 FTEs redeployed to higher-value claims analysis tasks"],
            metrics: [{ label: "Sorting Time", value: "-85%" }, { label: "Accuracy", value: "94%" }, { label: "Processing Delay", value: "-96%" }, { label: "FTEs Freed", value: "3" }],
            technologies: ["UiPath", "AI"]
          }
        },
        {
          title: "LLM-Powered Intelligent OCR",
          description: "Leveraged Large Language Models with OCR to extract data from highly unstructured documents with higher accuracy.",
          fullStory: {
            client: "European Freight & Customs Broker",
            industry: "Transportation & Logistics",
            challenge: "The customs team processed thousands of unstructured shipping documents — bills of lading, customs declarations, supplier invoices — in 9 different languages and layouts. Traditional OCR tools achieved only 60–65% extraction accuracy, requiring extensive manual correction and causing compliance risks.",
            solution: "We combined Azure Form Recognizer for initial OCR with GPT-4 for contextual extraction and validation. The LLM interprets ambiguous fields, normalises data formats across languages, and flags anomalies for human review. Extracted data feeds directly into the customs management system via API.",
            results: ["Extraction accuracy improved from 62% to 91%", "Manual correction effort reduced by 78%", "Processing throughput increased 3x — from 200 to 600 documents/day", "Compliance error rate dropped to near zero"],
            metrics: [{ label: "Accuracy", value: "91%" }, { label: "Manual Effort", value: "-78%" }, { label: "Throughput", value: "3x" }, { label: "Languages", value: "9" }],
            technologies: ["AI", "Python"]
          }
        }
      ]
    },
    {
      title: "AI Solutions",
      description: "Leverage artificial intelligence and machine learning to transform data into actionable insights.",
      IconComponent: AISVG,
      fullDescription: "Our AI solutions combine advanced machine learning, predictive modeling, and data visualization to help you uncover patterns and automate complex decision-making.",
      benefits: ["Uncover hidden patterns and correlations", "Build predictive models for future trends", "Automate complex decision-making", "Create interactive dashboards for real-time insights"],
      metrics: { ftes: "4.8", profit: "€145,660", roi: "293%", actions: "2,500", accuracy: "99%", employeeCost: "€185" },
      technologies: ['AI', 'Python'],
      useCase: "Automation of PDF document generation according to specific project standards with dynamic elements and content personalization.",
      why: "Our AI-powered solution combined advanced natural language processing with dynamic template management.",
      caseStudies: [
        {
          title: "ChatGPT RAG Assistant for Support",
          description: "Implemented RAG system using ChatGPT for L1/L2 operators to quickly find verified solutions.",
          fullStory: {
            client: "Telecommunications Provider — Service Desk",
            industry: "Telecommunications",
            challenge: "L1/L2 support operators handled 600+ tickets per day but spent 35% of resolution time searching through a fragmented knowledge base of 4,000+ articles, internal wikis, and PDF manuals. Average handle time was 18 minutes and first-call resolution stood at only 58%.",
            solution: "We built a RAG (Retrieval-Augmented Generation) system indexing the company's entire knowledge base using vector embeddings. Operators access a Teams-integrated chatbot — they describe the issue in natural language, and the system retrieves the 3 most relevant verified solutions with confidence scores and source citations, powered by GPT-4.",
            results: ["Average handle time reduced from 18 to 11 minutes (-39%)", "First-call resolution improved from 58% to 79%", "Operator onboarding time cut by 50% — new agents productive in 2 weeks vs. 4", "Knowledge base utilisation increased from 22% to 81% of available articles"],
            metrics: [{ label: "Handle Time", value: "-39%" }, { label: "First-Call Res.", value: "79%" }, { label: "Onboarding", value: "-50%" }, { label: "KB Usage", value: "+59pts" }],
            technologies: ["AI", "Python"]
          }
        },
        {
          title: "Automated Analysis of Customer Offers",
          description: "Used AI to parse and analyze RFPs, extracting key requirements and identifying potential risks.",
          fullStory: {
            client: "IT Services Company — Bid Management Team",
            industry: "Professional Services",
            challenge: "The bid team received 15–25 RFPs per month, each 50–200 pages long. Manually reviewing each document to extract requirements, spot risks, and map to capabilities took 2–3 days per bid. This bottleneck caused missed deadlines and limited the number of bids the team could pursue.",
            solution: "We developed a Python + GPT-4 pipeline that ingests RFP documents (PDF/Word), extracts structured requirement lists, flags non-standard clauses, scores alignment with company capabilities, and generates a structured bid-readiness summary report. The full analysis runs in under 8 minutes per document.",
            results: ["RFP analysis time reduced from 2–3 days to under 15 minutes", "Bid volume capacity increased by 60% — team now pursues 40 bids/month", "Risk flag accuracy validated at 88% against expert review", "Win rate improved by 18% due to better-qualified bid selection"],
            metrics: [{ label: "Analysis Time", value: "-99%" }, { label: "Bid Capacity", value: "+60%" }, { label: "Risk Accuracy", value: "88%" }, { label: "Win Rate", value: "+18%" }],
            technologies: ["AI", "Python"]
          }
        },
        {
          title: "Personalized Sales Pitch Generation",
          description: "Created AI system that analyzes prospect data to generate tailored talking points.",
          fullStory: {
            client: "B2B SaaS Sales Team — 45 Account Executives",
            industry: "Technology",
            challenge: "Sales AEs spent 45–90 minutes per account preparing personalised outreach — researching the prospect's industry, challenges, recent news, and crafting relevant talking points. Most reps relied on generic templates instead, resulting in low engagement and conversion rates.",
            solution: "We built an AI-powered pitch generator integrated with the CRM and LinkedIn. Given a prospect's name and company, the system aggregates public data, recent company news, LinkedIn activity, and industry trends, then uses GPT-4 to generate a personalised 3-point pitch narrative and subject line suggestions — ready in 90 seconds.",
            results: ["Pitch preparation time reduced from 60 minutes to under 2 minutes", "Email open rates increased by 34% (A/B tested over 3 months)", "Conversion to first meeting improved by 28%", "AEs reported reclaiming 3–5 hours per week for pipeline-building activities"],
            metrics: [{ label: "Prep Time", value: "-97%" }, { label: "Open Rate", value: "+34%" }, { label: "Meeting Conv.", value: "+28%" }, { label: "Time Reclaimed", value: "4h/wk" }],
            technologies: ["AI", "Python"]
          }
        }
      ]
    },
    {
      title: "Dashboard Development",
      description: "Create real-time dashboards for teams to monitor key statistics and performance indicators.",
      IconComponent: DashboardSVG,
      fullDescription: "We design and deploy interactive Power BI dashboards for monitoring key performance indicators across sales and support teams with real-time tracking and automated notifications.",
      benefits: ["Real-time visibility into key metrics", "Improved data-driven decision making", "Consolidated view of performance", "Faster identification of issues and trends"],
      metrics: { ftes: "Increased", profit: "Efficiency Gains", roi: "Improved Insights", actions: "Data Points Visualized", accuracy: "Real-time Updates" },
      technologies: ['Power BI'],
      useCase: "Interactive Power BI dashboards for monitoring KPIs across teams with automated email/Teams notifications for threshold breaches.",
      why: "Our dashboard solution combined real-time data visualization with automated alerting and incident creation.",
      caseStudies: [
        {
          title: "Real-time Alert Monitoring with Grafana",
          description: "Built Grafana dashboards integrating with system monitoring tools for faster response and resolution.",
          fullStory: {
            client: "SaaS Platform Provider",
            industry: "Technology",
            challenge: "The operations team relied on fragmented monitoring tools with no unified view. Engineers toggled between 5 different consoles to assess system health, leading to slow incident detection, duplicated alerts, and mean time-to-resolve (MTTR) of over 45 minutes.",
            solution: "We designed and deployed a centralised Grafana observability stack, pulling metrics from Prometheus, application logs, and infrastructure data into a single pane of glass. Custom alert rules trigger PagerDuty escalations and create automated incident tickets, providing end-to-end visibility across all services.",
            results: ["MTTR reduced from 45 minutes to 11 minutes", "Incident detection time cut by 70%", "Engineers consolidated from 5 tools to 1 unified dashboard", "On-call fatigue reduced — false positive alerts down by 60%"],
            metrics: [{ label: "MTTR Reduction", value: "-75%" }, { label: "Detection Speed", value: "+70%" }, { label: "Tools Replaced", value: "5→1" }, { label: "False Positives", value: "-60%" }],
            technologies: ["Power BI"]
          }
        },
        {
          title: "ELK Stack for Business KPIs",
          description: "Implemented ELK stack dashboards to track major business statistics and KPIs from various applications.",
          fullStory: {
            client: "Retail Group with 80+ Locations",
            industry: "Retail",
            challenge: "Business leadership had no real-time visibility into sales performance, inventory levels, or customer satisfaction — data lived in 6 disconnected systems. Weekly performance reports took 2 days to compile manually and were often outdated by the time decisions were made.",
            solution: "We deployed an ELK (Elasticsearch, Logstash, Kibana) stack to aggregate data from POS systems, e-commerce, ERP, and CRM in real time. Custom Kibana dashboards surface KPIs per location, category, and time period, enabling drill-down from chain-wide performance to individual store metrics.",
            results: ["Weekly reporting time cut from 2 days to zero — fully automated", "Leadership now accesses live KPIs within a 5-minute data lag", "Identified 3 underperforming store locations, driving €280k in targeted interventions", "Inventory discrepancy detection improved by 40%"],
            metrics: [{ label: "Report Time", value: "0 days" }, { label: "Data Lag", value: "5 min" }, { label: "Revenue Impact", value: "€280k" }, { label: "Accuracy", value: "+40%" }],
            technologies: ["Power BI", "Python"]
          }
        },
        {
          title: "Automated Statistic Notifications",
          description: "Created automated notification system linked to key dashboard metrics for critical statistics and alerts.",
          fullStory: {
            client: "Financial Services Operations Team",
            industry: "Banking & Insurance",
            challenge: "Operational managers had to manually check dashboards multiple times a day to catch SLA breaches, volume spikes, and anomalies. Important thresholds were regularly missed during busy periods, leading to delayed escalations and reporting gaps.",
            solution: "We built a proactive notification engine integrated with Power BI and Teams. Configurable thresholds trigger personalised alerts sent directly to relevant stakeholders via Teams messages and email — including contextual data snapshots, trend indicators, and recommended actions. Managers now receive intelligence, not noise.",
            results: ["100% of critical threshold breaches now detected and escalated automatically", "Management dashboard check time reduced by 80%", "Escalation response time improved by 55%", "Notification system now covers 18 distinct KPI thresholds"],
            metrics: [{ label: "Breach Detection", value: "100%" }, { label: "Check Time", value: "-80%" }, { label: "Response Speed", value: "+55%" }, { label: "KPIs Monitored", value: "18" }],
            technologies: ["Power BI"]
          }
        }
      ]
    },
    {
      title: "Application Development",
      description: "Connect disparate systems without API development for seamless data flow and processing.",
      IconComponent: AppDevelopmentSVG,
      fullDescription: "Our System Integration solutions use RPA as a non-invasive way to connect systems that lack APIs or where traditional integration would be too costly.",
      benefits: ["Connect legacy systems with modern applications", "Implement integration without changing existing systems", "Reduce integration costs by up to 60%", "Implement quickly compared to traditional methods"],
      metrics: { ftes: "2.5", profit: "€63,450", roi: "166%", actions: "510", accuracy: "100%", employeeCost: "€185" },
      technologies: ['Power Platform', 'Python'],
      useCase: "A comprehensive solution for managing business expenses and travel with integrated travel planning, expense approval, and reporting.",
      why: "Our application development solution provided a comprehensive, integrated approach to expense management.",
      caseStudies: [
        {
          title: "Company Event QR Code App",
          description: "Developed mobile-friendly web application generating unique QR codes for attendees at company events.",
          fullStory: {
            client: "Global Professional Services Firm",
            industry: "Professional Services",
            challenge: "Managing attendance at company events with 500+ participants involved manual sign-in sheets, paper badges, and lengthy registration queues. Organisers had no real-time visibility into attendance and post-event reporting took days to compile.",
            solution: "We built a lightweight React web app integrated with the company's HR directory. Each registered attendee receives a unique QR code by email. On the day, scanners at entry points check in attendees in under 2 seconds, with live attendance dashboards visible to organisers on any device.",
            results: ["Registration time reduced from 5 minutes to under 10 seconds per attendee", "Live attendance dashboard eliminated manual counting entirely", "Post-event reports generated automatically in real time", "Deployed and live within 3 weeks of project kickoff"],
            metrics: [{ label: "Check-in Speed", value: "10 sec" }, { label: "Manual Work", value: "0" }, { label: "Delivery Time", value: "3 weeks" }, { label: "Capacity", value: "500+" }],
            technologies: ["Power Platform", "Python"]
          }
        },
        {
          title: "Training Document Reformatting Tool",
          description: "Created application to automatically reformat training materials into standardized templates.",
          fullStory: {
            client: "Corporate Training & L&D Department",
            industry: "Healthcare",
            challenge: "The L&D team maintained 200+ training modules created over years by different authors in inconsistent formats. Converting legacy content to the new branded template took 2–4 hours per document and had become a months-long backlog that prevented a planned LMS migration.",
            solution: "We built a Python-based desktop application that parses legacy Word and PowerPoint files, maps content sections using NLP, and regenerates documents in the standardised template. The tool handles images, tables, bullet structures, and preserves semantic hierarchy — with a human-review queue for edge cases.",
            results: ["Backlog of 200 documents processed in 4 days vs. estimated 6 months manually", "Format conversion accuracy of 96% — far exceeding manual consistency", "L&D team freed to focus on content quality rather than formatting", "LMS migration unblocked and delivered on schedule"],
            metrics: [{ label: "Docs Processed", value: "200" }, { label: "Time Saved", value: "6 months" }, { label: "Accuracy", value: "96%" }, { label: "Migration", value: "On time" }],
            technologies: ["Python", "Power Platform"]
          }
        },
        {
          title: "Company-Wide Overtime Management System",
          description: "Built centralized application for employees to request and managers to approve overtime hours.",
          fullStory: {
            client: "Manufacturing Group — 1,200 Employees",
            industry: "Manufacturing",
            challenge: "Overtime requests were handled via email chains and spreadsheets across 8 departments. Approval workflows were inconsistent, hours were frequently miscalculated, and payroll had to reconcile data manually every month — a process taking 3 days and riddled with disputes.",
            solution: "We developed a Power Apps solution integrated with SharePoint and Teams. Employees submit overtime requests via a mobile-friendly form. Automated approval workflows route requests to the correct manager tier, apply company policy rules, and sync approved hours directly to the payroll preparation sheet.",
            results: ["Monthly payroll reconciliation time cut from 3 days to 4 hours", "Zero overtime disputes in the 6 months post-deployment (vs. avg. 12/month)", "Full audit trail now available for every overtime request", "100% policy compliance enforced automatically"],
            metrics: [{ label: "Payroll Time", value: "-87%" }, { label: "Disputes", value: "0/mo" }, { label: "Compliance", value: "100%" }, { label: "Users", value: "1,200" }],
            technologies: ["Power Platform"]
          }
        }
      ]
    }
  ];

  const technologyDetails = {
    'UiPath': {
      description: "UiPath is a leading RPA platform designed to automate repetitive and rule-based tasks with a user-friendly interface.",
      whenToUse: ["For automating highly repetitive, manual tasks", "When non-intrusive automation is needed", "For enterprise-scale RPA projects", "For tasks requiring OCR or document processing"]
    },
    'Python': {
      description: "Python is a versatile programming language known for its simplicity and extensive library support for data manipulation and ML.",
      whenToUse: ["When custom solutions or flexibility are needed", "For data analysis and machine learning workflows", "To integrate diverse systems efficiently", "When scalability is a concern"]
    },
    'PowerShell': {
      description: "PowerShell is a task automation framework primarily used for managing Windows environments and system administration.",
      whenToUse: ["For system administration and server management", "When working in Windows environments", "To automate IT tasks like backups and monitoring", "For tasks requiring direct system access"]
    },
    'Power Platform': {
      description: "Microsoft Power Platform enables low-code/no-code development for process automation and application creation.",
      whenToUse: ["For automating workflows within Microsoft ecosystem", "When rapid development is needed", "For non-technical users to build solutions", "To connect Microsoft services and third-party apps"]
    },
    'AI': {
      description: "AI leverages machine learning and NLP to enable intelligent decision-making and predictive analytics.",
      whenToUse: ["To process unstructured data like text and images", "When tasks involve complex pattern recognition", "For enhancing customer interactions with chatbots", "To optimize processes with predictive analytics"]
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
        
        {/* Animated Header */}
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <Box sx={{ 
            bgcolor: 'rgba(35, 31, 32, 0.95)', 
            backdropFilter: 'blur(20px)',
            color: 'white', 
            py: 2, 
            position: 'sticky', 
            top: 0, 
            zIndex: 1000,
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}>
            <Container maxWidth="xl">
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
                    <Box sx={{ 
                      bgcolor: 'white', 
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      height: '44px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                    }}>
                      <img src={SopraLogo} alt="Sopra Steria Logo" style={{ height: '100%', width: 'auto', objectFit: 'contain' }} />
                    </Box>
                  </motion.div>
                  <Typography variant="h5" component="h1" sx={{ 
                    fontWeight: 'bold',
                    background: 'linear-gradient(90deg, #fff 0%, #f47216 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '-0.02em'
                  }}>
                    Automation Factory
                  </Typography>
                </Grid>
                <Grid item>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="contained"
                      onClick={handleOpenContactDialog}
                      startIcon={<RocketLaunchIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #f47216 0%, #db1d2b 100%)',
                        py: 1.5,
                        px: 4,
                        fontSize: '0.95rem',
                        boxShadow: '0 4px 20px rgba(244, 114, 22, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #ff8c3a 0%, #ff3d4d 100%)',
                          boxShadow: '0 6px 30px rgba(244, 114, 22, 0.5)',
                        }
                      }}
                    >
                      Contact Expert
                    </Button>
                  </motion.div>
                </Grid>
              </Grid>
            </Container>
          </Box>
        </motion.div>

        {/* Hero Section */}
        <Box sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          position: 'relative',
          px: 2,
          py: 8
        }}>
          <AnimatedBackground showParticles showGrid showOrbs showCircuits />
          
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }} ref={statsRef}>
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              
              {/* Company Logos */}
              <motion.div variants={fadeInUp}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 4,
                  justifyContent: 'center',
                  mb: 6
                }}>
                  {/* Sopra Steria Card */}
                  <GlassCard sx={{ flex: 1, maxWidth: '400px', p: 4, textAlign: 'center' }}>
                    <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                      <Box sx={{
                        width: '140px',
                        height: '140px',
                        margin: '0 auto 20px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(219,29,43,0.1) 0%, rgba(244,114,22,0.1) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 40px rgba(219,29,43,0.2)'
                      }}>
                        <img src={SopraLogo} alt="Sopra Steria" style={{ width: '80%', objectFit: 'contain' }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.primary.main }}>
                        Sopra Steria
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
                        A European leader in consulting and digital services, driving digital transformation.
                      </Typography>
                    </motion.div>
                  </GlassCard>

                  {/* Automation Factory Card */}
                  <GlassCard sx={{ flex: 1, maxWidth: '400px', p: 4, textAlign: 'center' }}>
                    <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.3 }}>
                      <Box sx={{
                        width: '140px',
                        height: '140px',
                        margin: '0 auto 20px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(244,114,22,0.1) 0%, rgba(219,29,43,0.1) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 40px rgba(244,114,22,0.2)'
                      }}>
                        <img src={AFLogo} alt="Automation Factory" style={{ width: '80%', objectFit: 'contain' }} />
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: theme.palette.secondary.main }}>
                        Automation Factory
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7 }}>
                        Specialized in delivering innovative automation solutions that drive efficiency.
                      </Typography>
                    </motion.div>
                  </GlassCard>
                </Box>
              </motion.div>

              {/* Main Title */}
              <motion.div variants={fadeInUp}>
                <Typography variant="h1" sx={{ 
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  fontWeight: 800,
                  mb: 2,
                  background: 'linear-gradient(135deg, #231f20 0%, #db1d2b 50%, #f47216 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.03em'
                }}>
                  Automation Factory
                </Typography>
                <Typography variant="h4" sx={{ 
                  mb: 2, 
                  color: '#5a6775', 
                  fontWeight: 400,
                  fontSize: { xs: '1.2rem', md: '1.8rem' }
                }}>
                  Calculate your ROI with innovative automation solutions
                </Typography>
              </motion.div>

              {/* Stats Cards */}
              <motion.div variants={fadeInUp}>
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'center',
                  gap: 4,
                  my: 6
                }}>
                  <motion.div whileHover={{ y: -10, scale: 1.03 }} transition={{ duration: 0.3 }}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, #db1d2b 0%, #ff4d5a 100%)',
                      color: 'white',
                      minWidth: { xs: '100%', sm: '280px' },
                      boxShadow: '0 20px 40px rgba(219,29,43,0.3)',
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h2" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '2.8rem', md: '3.5rem' } }}>
                          {animateStats ? <CountUp end={57.2} duration={3} decimals={1} suffix="+" /> : '0'}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9, letterSpacing: '0.05em' }}>
                          SAVED FTE
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div whileHover={{ y: -10, scale: 1.03 }} transition={{ duration: 0.3 }}>
                    <Card sx={{ 
                      background: 'linear-gradient(135deg, #f47216 0%, #ff9344 100%)',
                      color: 'white',
                      minWidth: { xs: '100%', sm: '280px' },
                      boxShadow: '0 20px 40px rgba(244,114,22,0.3)',
                    }}>
                      <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h2" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '2.8rem', md: '3.5rem' } }}>
                          {animateStats ? <CountUp end={500} duration={3} suffix="+" /> : '0'}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9, letterSpacing: '0.05em' }}>
                          AUTOMATIONS
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Box>
              </motion.div>

              {/* Trusted Customers */}
              <motion.div variants={fadeInUp}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: '#231f20' }}>
                  Trusted by Industry Leaders
                </Typography>
                <Box sx={{ 
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 4,
                  mb: 6
                }}>
                  {[
                    { logo: EuropcarLogo, name: 'Europcar Mobility Group' },
                    { logo: LaPosteLogo, name: 'La Poste' },
                    { logo: CevaLogo, name: 'CEVA Logistics' }
                  ].map((partner, index) => (
                    <motion.div 
                      key={index}
                      whileHover={{ y: -8, scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CompanyTooltip company={partner.name}>
                        <GlassCard sx={{ 
                          width: '200px',
                          height: '100px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 2,
                          cursor: 'pointer'
                        }}>
                          <img src={partner.logo} alt={partner.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </GlassCard>
                      </CompanyTooltip>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>

              {/* Partners */}
              <motion.div variants={fadeInUp}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: '#231f20' }}>
                  Partners
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 6 }}>
                  {[UiPathPartnerLogo, MSPartnerLogo].map((logo, index) => (
                    <motion.div key={index} whileHover={{ y: -8, scale: 1.05 }} transition={{ duration: 0.3 }}>
                      <GlassCard sx={{ 
                        width: '200px',
                        height: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2
                      }}>
                        <img src={logo} alt="Partner" style={{ maxWidth: '100%', maxHeight: '70px', objectFit: 'contain' }} />
                      </GlassCard>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>

              {/* Technologies */}
              <motion.div variants={fadeInUp}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, color: '#231f20' }}>
                  Technologies
                </Typography>
                <Box sx={{ 
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 3,
                  mb: 6
                }}>
                  {['UiPath', 'Python', 'PowerShell', 'Power Platform', 'AI'].map((tech, index) => (
                    <MuiTooltip
                      key={index}
                      title={
                        <Box sx={{ p: 2, maxWidth: '400px' }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.primary.main }}>
                            {tech}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                            {technologyDetails[tech]?.description}
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main, mb: 1 }}>
                            When to Use:
                          </Typography>
                          <List dense sx={{ p: 0 }}>
                            {technologyDetails[tech]?.whenToUse.map((use, i) => (
                              <ListItem key={i} sx={{ py: 0.25 }}>
                                <ListItemIcon sx={{ minWidth: '24px' }}>🎯</ListItemIcon>
                                <ListItemText 
                                  primary={use} 
                                  primaryTypographyProps={{ 
                                    fontSize: '0.85rem',
                                    sx: { color: '#231f20' }
                                  }} 
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      }
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: { sx: { bgcolor: 'white', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', borderRadius: 2, p: 0, maxWidth: 'none' } }
                      }}
                    >
                      <motion.div whileHover={{ y: -8, scale: 1.1 }} transition={{ duration: 0.3 }}>
                        <GlassCard sx={{ 
                          width: '110px',
                          height: '110px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 2,
                          cursor: 'pointer'
                        }}>
                          <Box sx={{ height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                            {tech === 'UiPath' && <img src={UiPathLogo} alt={tech} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                            {tech === 'Python' && <img src={PythonLogo} alt={tech} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                            {tech === 'PowerShell' && <img src={PowerShellLogo} alt={tech} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                            {tech === 'Power Platform' && <img src={PowerPlatformLogo} alt={tech} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                            {tech === 'AI' && <img src={AILogo} alt={tech} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                          </Box>
                          <Typography variant="caption" sx={{ 
                            fontWeight: 600, 
                            color: '#231f20',
                            textAlign: 'center',
                            fontSize: '0.75rem'
                          }}>
                            {tech}
                          </Typography>
                        </GlassCard>
                      </motion.div>
                    </MuiTooltip>
                  ))}
                </Box>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div variants={fadeInUp}>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={scrollToServices}
                      sx={{ 
                        background: 'linear-gradient(135deg, #db1d2b 0%, #ff4d5a 100%)',
                        py: 2, 
                        px: 5, 
                        fontSize: '1.1rem',
                        boxShadow: '0 8px 30px rgba(219,29,43,0.4)',
                        '&:hover': {
                          boxShadow: '0 12px 40px rgba(219,29,43,0.5)',
                        }
                      }}
                    >
                      Explore Our Services
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button 
                      variant="outlined" 
                      size="large"
                      onClick={scrollToCalculator}
                      startIcon={<AutoGraphIcon />}
                      sx={{ 
                        borderColor: '#f47216',
                        borderWidth: 2,
                        color: '#f47216',
                        py: 2, 
                        px: 5, 
                        fontSize: '1.1rem',
                        '&:hover': {
                          borderColor: '#db1d2b',
                          borderWidth: 2,
                          color: '#db1d2b',
                          bgcolor: 'rgba(219,29,43,0.05)',
                        }
                      }}
                    >
                      Calculate Your ROI
                    </Button>
                  </motion.div>
                </Box>
              </motion.div>
            </motion.div>
          </Container>
        </Box>

        {/* Service Catalog Section */}
        <Box ref={servicesRef} sx={{ py: 10, position: 'relative', bgcolor: '#fafafa' }}>
          <Box sx={{ 
            position: 'absolute', 
            inset: 0, 
            backgroundImage: 'linear-gradient(rgba(244, 114, 22, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(244, 114, 22, 0.02) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }} />
          
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography variant="h2" sx={{ 
                fontWeight: 800, 
                mb: 2, 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #231f20 0%, #db1d2b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Our Automation Services
              </Typography>
              <Typography variant="h6" sx={{ mb: 8, textAlign: 'center', color: '#5a6775', maxWidth: '700px', mx: 'auto' }}>
                Discover how our tailored automation solutions can transform your business processes
              </Typography>
            </motion.div>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 3,
              gridAutoRows: '1fr',
            }}>
              {serviceItems.map((service, index) => (
                <Box key={index} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <motion.div whileHover={{ y: -12, scale: 1.02 }} transition={{ duration: 0.3 }} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Card sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.5)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        overflow: 'visible',
                        '&:hover': {
                          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                        }
                      }}>
                        <CardActionArea 
                          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start', p: 3 }}
                          onClick={() => handleServiceClick(service)}
                        >
                          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                            <service.IconComponent size={80} color="#f47216" />
                          </Box>
                          <CardContent sx={{ flexGrow: 1, pt: 0, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#231f20', textAlign: 'center', mb: 2, flexShrink: 0 }}>
                              {service.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#5a6775', textAlign: 'center', lineHeight: 1.7, flexGrow: 1 }}>
                              {service.description}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                        
                        <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleServiceClick(service); }}
                              sx={{
                                borderRadius: 20,
                                borderColor: '#f47216',
                                color: '#f47216',
                                fontWeight: 600,
                                px: 3,
                                '&:hover': {
                                  borderColor: '#db1d2b',
                                  bgcolor: 'rgba(219,29,43,0.05)',
                                  color: '#db1d2b',
                                }
                              }}
                            >
                              📊 View Case Studies
                            </Button>
                          </motion.div>
                        </Box>
                      </Card>
                    </motion.div>
                  </motion.div>
                </Box>
              ))}
            </Box>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Box sx={{ textAlign: 'center', mt: 8 }}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    onClick={scrollToCalculator}
                    sx={{ 
                      background: 'linear-gradient(135deg, #f47216 0%, #db1d2b 100%)',
                      py: 2, 
                      px: 5, 
                      fontSize: '1.1rem',
                      boxShadow: '0 8px 30px rgba(244,114,22,0.4)',
                    }}
                  >
                    Calculate ROI for Your Business
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          </Container>
        </Box>

        {/* ROI Calculator Section */}
        <Box ref={calculatorRef} sx={{ py: 10, position: 'relative' }}>
          <AnimatedBackground showParticles={false} showGrid showOrbs />
          
          <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography variant="h2" sx={{ 
                mb: 2, 
                fontWeight: 800, 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #231f20 0%, #f47216 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                ROI Calculator
              </Typography>
              <Typography variant="h6" sx={{ mb: 8, textAlign: 'center', color: '#5a6775', maxWidth: '700px', mx: 'auto' }}>
                Estimate the potential financial benefits of implementing Automation Factory
              </Typography>
            </motion.div>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              alignItems: { md: 'flex-start' },
              gap: 4
            }}>
              {/* Calculator */}
              <motion.div 
                style={{ flex: 1, width: '100%' }}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <GlassCard sx={{ p: 4 }}>
                  <ROICalculator onCalculate={handleCalculate} />
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="contained"
                        onClick={handleOpenContactDialog}
                        sx={{
                          background: 'linear-gradient(135deg, #f47216 0%, #db1d2b 100%)',
                          py: 1.5,
                          px: 4,
                          fontWeight: 600,
                        }}
                      >
                        Contact an Automation Expert
                      </Button>
                    </motion.div>
                  </Box>
                </GlassCard>
              </motion.div>
              
              {/* Results */}
              <Box sx={{ 
                flex: 1,
                width: '100%',
                alignSelf: { md: 'flex-start' },
                position: { md: 'sticky' },
                top: { md: 20 },
                height: { md: 'fit-content' },
                zIndex: 10
              }}>
                <motion.div 
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <GlassCard sx={{ p: 4 }}>
                    <ResultsDisplay data={calculationData} />
                  
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="contained"
                        onClick={handleGenerateReport}
                        disabled={!calculationData}
                        sx={{
                          background: 'linear-gradient(135deg, #f47216 0%, #db1d2b 100%)',
                          py: 1.5,
                          px: 3,
                          fontWeight: 600,
                        }}
                      >
                        📄 Generate PDF Report
                      </Button>
                    </motion.div>
                    
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        variant="outlined"
                        onClick={handleOpenContactDialog}
                        sx={{
                          borderColor: '#f47216',
                          borderWidth: 2,
                          color: '#f47216',
                          py: 1.5,
                          px: 3,
                          fontWeight: 600,
                          '&:hover': {
                            borderColor: '#db1d2b',
                            borderWidth: 2,
                            color: '#db1d2b',
                          }
                        }}
                      >
                        Contact an Expert
                      </Button>
                    </motion.div>
                  </Box>
                </GlassCard>
                </motion.div>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Floating Back Button */}
        <AnimatePresence>
          {selectedService && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              style={{
                position: 'fixed',
                bottom: '40px',
                right: '40px',
                zIndex: 1100,
              }}
            >
              <Button
                variant="contained"
                onClick={() => { handleCloseServiceDetails(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                startIcon={<ArrowUpwardIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #db1d2b 0%, #f47216 100%)',
                  borderRadius: '50px',
                  px: 3,
                  py: 1.5,
                  boxShadow: '0 8px 30px rgba(219,29,43,0.4)',
                }}
              >
                Back to Main
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Service Details Slide */}
        <Slide direction="up" in={selectedService !== null} mountOnEnter unmountOnExit>
          <Box sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            bgcolor: 'white', 
            zIndex: 1200,
            overflowY: 'auto',
          }}>
            {selectedService && (
              <Box sx={{ position: 'relative' }}>
                <AnimatedBackground showParticles={false} showGrid showOrbs />
                
                <Container maxWidth="lg" sx={{ py: 6, position: 'relative', zIndex: 1 }}>
                  {/* Back Button */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    style={{ position: 'fixed', top: '40px', left: '40px', zIndex: 1300, display: 'flex', gap: 16 }}
                  >
                    <Button 
                      variant="contained"
                      startIcon={<ArrowBackIcon />} 
                      onClick={handleCloseServiceDetails}
                      sx={{ 
                        background: 'linear-gradient(135deg, #db1d2b 0%, #f47216 100%)',
                        borderRadius: '50px',
                        px: 3,
                        py: 1.5,
                        boxShadow: '0 8px 30px rgba(219,29,43,0.3)',
                      }}
                    >
                      Back to Services
                    </Button>
                    <IconButton 
                      onClick={handleCloseServiceDetails}
                      sx={{ 
                        bgcolor: 'white',
                        color: theme.palette.primary.main,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        '&:hover': { bgcolor: theme.palette.primary.main, color: 'white' },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </motion.div>

                  <Box sx={{ mt: 8 }}>
                    {/* Hero Header */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      <GlassCard sx={{ 
                        p: { xs: 4, md: 6 }, 
                        mb: 5,
                        background: 'linear-gradient(135deg, rgba(219,29,43,0.03) 0%, rgba(244,114,22,0.03) 100%)',
                        border: '1px solid rgba(219,29,43,0.1)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}>
                        {/* Background decoration */}
                        <Box sx={{
                          position: 'absolute',
                          top: -50,
                          right: -50,
                          width: 200,
                          height: 200,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, rgba(244,114,22,0.1) 0%, rgba(219,29,43,0.05) 100%)',
                          filter: 'blur(40px)'
                        }} />
                        
                        <Grid container spacing={4} alignItems="center">
                          <Grid item xs={12} md={2}>
                            <motion.div whileHover={{ scale: 1.1, rotate: 5 }} transition={{ duration: 0.3 }}>
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: { xs: 'center', md: 'flex-start' },
                                p: 2,
                                borderRadius: 4,
                                background: 'white',
                                boxShadow: '0 10px 40px rgba(244,114,22,0.15)',
                                width: 'fit-content',
                                mx: { xs: 'auto', md: 0 }
                              }}>
                                <selectedService.IconComponent size={80} color="#f47216" />
                              </Box>
                            </motion.div>
                          </Grid>
                          <Grid item xs={12} md={10}>
                            <Typography variant="h2" sx={{ 
                              fontWeight: 800, 
                              mb: 2,
                              textAlign: { xs: 'center', md: 'left' },
                              background: 'linear-gradient(135deg, #231f20 0%, #db1d2b 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              fontSize: { xs: '2rem', md: '3rem' }
                            }}>
                              {selectedService.title}
                            </Typography>
                            <Typography variant="h6" sx={{ 
                              color: '#5a6775', 
                              textAlign: { xs: 'center', md: 'left' },
                              maxWidth: '700px'
                            }}>
                              {selectedService.description}
                            </Typography>
                          </Grid>
                        </Grid>
                      </GlassCard>
                    </motion.div>

                    {/* Stats Banner */}
                    {(selectedService.metrics?.ftes || selectedService.metrics?.roi || selectedService.metrics?.accuracy || selectedService.technologies?.length > 0) && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 }}
                    >
                      <Grid container spacing={3} sx={{ mb: 5 }}>
                        {selectedService.metrics?.ftes && (
                          <Grid item xs={6} md={3}>
                            <GlassCard sx={{ 
                              p: 3, 
                              textAlign: 'center',
                              background: 'linear-gradient(135deg, rgba(219,29,43,0.05) 0%, rgba(219,29,43,0.02) 100%)',
                              border: '1px solid rgba(219,29,43,0.1)',
                              height: '100%'
                            }}>
                              <Typography variant="h2" sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 1 }}>
                                {selectedService.metrics.ftes}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#5a6775', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                                FTEs Saved
                              </Typography>
                            </GlassCard>
                          </Grid>
                        )}
                        {selectedService.metrics?.roi && (
                          <Grid item xs={6} md={3}>
                            <GlassCard sx={{ 
                              p: 3, 
                              textAlign: 'center',
                              background: 'linear-gradient(135deg, rgba(244,114,22,0.05) 0%, rgba(244,114,22,0.02) 100%)',
                              border: '1px solid rgba(244,114,22,0.1)',
                              height: '100%'
                            }}>
                              <Typography variant="h2" sx={{ fontWeight: 800, color: theme.palette.secondary.main, mb: 1 }}>
                                {selectedService.metrics.roi}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#5a6775', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                                ROI Generated yearly
                              </Typography>
                            </GlassCard>
                          </Grid>
                        )}
                        {selectedService.metrics?.accuracy && (
                          <Grid item xs={6} md={3}>
                            <GlassCard sx={{ 
                              p: 3, 
                              textAlign: 'center',
                              background: 'linear-gradient(135deg, rgba(90,125,154,0.05) 0%, rgba(90,125,154,0.02) 100%)',
                              border: '1px solid rgba(90,125,154,0.1)',
                              height: '100%'
                            }}>
                              <Typography variant="h2" sx={{ fontWeight: 800, color: '#5a7d9a', mb: 1 }}>
                                {selectedService.metrics.accuracy}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#5a6775', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                                Accuracy
                              </Typography>
                            </GlassCard>
                          </Grid>
                        )}
                        {selectedService.technologies?.length > 0 && (
                        <Grid item xs={6} md={3}>
                          <GlassCard sx={{ 
                            p: 3, 
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, rgba(35,31,32,0.05) 0%, rgba(35,31,32,0.02) 100%)',
                            border: '1px solid rgba(35,31,32,0.1)',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'center', 
                              alignItems: 'center',
                              gap: 1, 
                              flexWrap: 'wrap',
                              flexGrow: 1
                            }}>
                              {selectedService.technologies.slice(0, 3).map((techName, techIndex) => {
                                let logoSrc = null;
                                if (techName === 'UiPath') logoSrc = UiPathLogo;
                                else if (techName === 'Python') logoSrc = PythonLogo;
                                else if (techName === 'PowerShell') logoSrc = PowerShellLogo;
                                else if (techName === 'Power Platform') logoSrc = PowerPlatformLogo;
                                else if (techName === 'AI') logoSrc = AILogo;
                                
                                return logoSrc ? (
                                  <MuiTooltip title={techName} key={techIndex}>
                                    <Box 
                                      component="img"
                                      src={logoSrc}
                                      alt={techName}
                                      sx={{ height: '35px', width: 'auto', objectFit: 'contain' }}
                                    />
                                  </MuiTooltip>
                                ) : null;
                              })}
                            </Box>
                            <Typography variant="body2" sx={{ 
                              color: '#5a6775', 
                              fontWeight: 600, 
                              textTransform: 'uppercase', 
                              letterSpacing: 1,
                              mt: 'auto'
                            }}>
                              Technologies
                            </Typography>
                          </GlassCard>
                        </Grid>
                        )}
                      </Grid>
                    </motion.div>
                    )}

                    {/* Tabbed Content Section */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <GlassCard sx={{ 
                        p: 0, 
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.08)',
                      }}>
                        {/* Tab Navigation */}
                        <Box sx={{ 
                          display: 'flex', 
                          borderBottom: '2px solid rgba(0,0,0,0.06)',
                          bgcolor: 'rgba(35,31,32,0.02)',
                          overflowX: 'auto',
                          '&::-webkit-scrollbar': { display: 'none' }
                        }}>
                          {[
                            { id: 'overview', label: 'Overview', icon: '📋' },
                            { id: 'case-study', label: 'Case Study', icon: '📊' },
                            { id: 'benefits', label: 'Key Benefits', icon: '⭐' },
                            { id: 'more-cases', label: 'More Stories', icon: '📁' },
                          ].map((tab) => (
                            <Box 
                              key={tab.id}
                              onClick={() => {
                                document.getElementById(`section-${tab.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}
                              sx={{ 
                                px: 4, 
                                py: 2.5, 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                cursor: 'pointer',
                                borderBottom: '3px solid transparent',
                                transition: 'all 0.3s ease',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  bgcolor: 'rgba(219,29,43,0.05)',
                                  borderBottomColor: theme.palette.primary.main,
                                }
                              }}
                            >
                              <Typography sx={{ fontSize: '1.2rem' }}>{tab.icon}</Typography>
                              <Typography sx={{ fontWeight: 600, color: '#231f20' }}>{tab.label}</Typography>
                            </Box>
                          ))}
                        </Box>

                        {/* Content Sections */}
                        <Box sx={{ p: { xs: 3, md: 5 } }}>
                          
                          {/* Overview Section */}
                          <Box id="section-overview" sx={{ mb: 6, scrollMarginTop: '100px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                              <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #db1d2b 0%, #ff4d5a 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 8px 25px rgba(219,29,43,0.25)'
                              }}>
                                <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>📋</Typography>
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#231f20' }}>
                                  Overview
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#5a6775' }}>
                                  What this service delivers
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ 
                              p: 4, 
                              borderRadius: 4, 
                              bgcolor: 'rgba(244,114,22,0.03)',
                              border: '1px solid rgba(244,114,22,0.1)'
                            }}>
                              <Typography variant="body1" sx={{ fontSize: '1.15rem', lineHeight: 2, color: '#5a6775' }}>
                                {selectedService.fullDescription}
                              </Typography>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 5, borderColor: 'rgba(0,0,0,0.06)' }} />

                          {/* Case Study Section */}
                          <Box id="section-case-study" sx={{ mb: 6, scrollMarginTop: '100px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                              <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #f47216 0%, #ff9344 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 8px 25px rgba(244,114,22,0.25)'
                              }}>
                                <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>📊</Typography>
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#231f20' }}>
                                  Case Study
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#5a6775' }}>
                                  Real-world implementation success
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Grid container spacing={3}>
                              {/* Challenge Card */}
                              <Grid item xs={12} md={4}>
                                <motion.div whileHover={{ y: -5 }} style={{ height: '100%' }}>
                                  <Box sx={{ 
                                    height: '100%',
                                    p: 4, 
                                    borderRadius: 4,
                                    background: 'linear-gradient(180deg, rgba(219,29,43,0.08) 0%, rgba(219,29,43,0.02) 100%)',
                                    border: '2px solid rgba(219,29,43,0.15)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                  }}>
                                    <Box sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: 4,
                                      background: 'linear-gradient(90deg, #db1d2b, #ff4d5a)'
                                    }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                      <Typography sx={{ fontSize: '2rem' }}>🎯</Typography>
                                      <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                                        The Challenge
                                      </Typography>
                                    </Box>
                                    <Typography variant="body1" sx={{ lineHeight: 1.9, color: '#5a6775' }}>
                                      The customer faced operational inefficiencies requiring significant manual effort, impacting productivity and costs.
                                    </Typography>
                                  </Box>
                                </motion.div>
                              </Grid>

                              {/* Solution Card */}
                              <Grid item xs={12} md={4}>
                                <motion.div whileHover={{ y: -5 }} style={{ height: '100%' }}>
                                  <Box sx={{ 
                                    height: '100%',
                                    p: 4, 
                                    borderRadius: 4,
                                    background: 'linear-gradient(180deg, rgba(244,114,22,0.08) 0%, rgba(244,114,22,0.02) 100%)',
                                    border: '2px solid rgba(244,114,22,0.15)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                  }}>
                                    <Box sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: 4,
                                      background: 'linear-gradient(90deg, #f47216, #ff9344)'
                                    }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                      <Typography sx={{ fontSize: '2rem' }}>💡</Typography>
                                      <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.secondary.main }}>
                                        Our Solution
                                      </Typography>
                                    </Box>
                                    <Typography variant="body1" sx={{ lineHeight: 1.9, color: '#5a6775' }}>
                                      {selectedService.useCase}
                                    </Typography>
                                  </Box>
                                </motion.div>
                              </Grid>

                              {/* Why Us Card */}
                              <Grid item xs={12} md={4}>
                                <motion.div whileHover={{ y: -5 }} style={{ height: '100%' }}>
                                  <Box sx={{ 
                                    height: '100%',
                                    p: 4, 
                                    borderRadius: 4,
                                    background: 'linear-gradient(180deg, rgba(90,125,154,0.08) 0%, rgba(90,125,154,0.02) 100%)',
                                    border: '2px solid rgba(90,125,154,0.15)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                  }}>
                                    <Box sx={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      height: 4,
                                      background: 'linear-gradient(90deg, #5a7d9a, #7ea3c1)'
                                    }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                      <Typography sx={{ fontSize: '2rem' }}>✨</Typography>
                                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#5a7d9a' }}>
                                        Why Us
                                      </Typography>
                                    </Box>
                                    <Typography variant="body1" sx={{ lineHeight: 1.9, color: '#5a6775' }}>
                                      {selectedService.why}
                                    </Typography>
                                  </Box>
                                </motion.div>
                              </Grid>
                            </Grid>
                          </Box>

                          <Divider sx={{ my: 5, borderColor: 'rgba(0,0,0,0.06)' }} />

                          {/* Key Benefits Section */}
                          <Box id="section-benefits" sx={{ mb: 6, scrollMarginTop: '100px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                              <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #231f20 0%, #5a6775 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 8px 25px rgba(35,31,32,0.25)'
                              }}>
                                <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>⭐</Typography>
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#231f20' }}>
                                  Key Benefits
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#5a6775' }}>
                                  What you'll gain from this service
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Grid container spacing={2}>
                              {selectedService.benefits?.map((benefit, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                  <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    whileHover={{ scale: 1.02, y: -3 }}
                                  >
                                    <Box sx={{ 
                                      p: 3, 
                                      borderRadius: 3, 
                                      bgcolor: 'white',
                                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                                      border: '1px solid rgba(0,0,0,0.06)',
                                      height: '100%',
                                      display: 'flex', 
                                      alignItems: 'flex-start', 
                                      gap: 2,
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        boxShadow: '0 8px 30px rgba(219,29,43,0.15)',
                                        borderColor: 'rgba(219,29,43,0.2)',
                                      }
                                    }}>
                                      <Box sx={{ 
                                        width: 40, 
                                        height: 40, 
                                        borderRadius: 2, 
                                        background: `linear-gradient(135deg, ${index % 3 === 0 ? '#db1d2b' : index % 3 === 1 ? '#f47216' : '#5a7d9a'} 0%, ${index % 3 === 0 ? '#ff4d5a' : index % 3 === 1 ? '#ff9344' : '#7ea3c1'} 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        fontWeight: 800,
                                        fontSize: '1rem',
                                        flexShrink: 0
                                      }}>
                                        {index + 1}
                                      </Box>
                                      <Typography variant="body1" sx={{ color: '#5a6775', fontWeight: 500, lineHeight: 1.7 }}>
                                        {benefit}
                                      </Typography>
                                    </Box>
                                  </motion.div>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Technologies */}
                            {selectedService.technologies?.length > 0 && (
                              <Box sx={{ 
                                mt: 4, 
                                p: 3, 
                                borderRadius: 3, 
                                bgcolor: 'rgba(35,31,32,0.03)',
                                border: '1px solid rgba(35,31,32,0.08)'
                              }}>
                                <Typography variant="subtitle2" sx={{ 
                                  mb: 2, 
                                  fontWeight: 700, 
                                  color: '#5a6775',
                                  textTransform: 'uppercase',
                                  letterSpacing: 1.5
                                }}>
                                  Powered By
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                  {selectedService.technologies.map((techName, techIndex) => {
                                    let logoSrc = null;
                                    if (techName === 'UiPath') logoSrc = UiPathLogo;
                                    else if (techName === 'Python') logoSrc = PythonLogo;
                                    else if (techName === 'PowerShell') logoSrc = PowerShellLogo;
                                    else if (techName === 'Power Platform') logoSrc = PowerPlatformLogo;
                                    else if (techName === 'AI') logoSrc = AILogo;
                                    
                                    return logoSrc ? (
                                      <motion.div key={techIndex} whileHover={{ scale: 1.1 }}>
                                        <MuiTooltip title={techName}>
                                          <Box sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: 'white',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5
                                          }}>
                                            <Box 
                                              component="img"
                                              src={logoSrc}
                                              alt={techName}
                                              sx={{ height: '28px', width: 'auto', objectFit: 'contain' }}
                                            />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#5a6775' }}>
                                              {techName}
                                            </Typography>
                                          </Box>
                                        </MuiTooltip>
                                      </motion.div>
                                    ) : null;
                                  })}
                                </Box>
                              </Box>
                            )}
                          </Box>

                          <Divider sx={{ my: 5, borderColor: 'rgba(0,0,0,0.06)' }} />

                          {/* More Success Stories Section */}
                          <Box id="section-more-cases" sx={{ scrollMarginTop: '100px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                              <Box sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #db1d2b 0%, #f47216 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 8px 25px rgba(219,29,43,0.25)'
                              }}>
                                <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>📁</Typography>
                              </Box>
                              <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#231f20' }}>
                                  More Success Stories
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#5a6775' }}>
                                  See how others benefited
                                </Typography>
                              </Box>
                            </Box>
                            
                            <Box sx={{ 
                              display: 'grid', 
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                              gap: 3 
                            }}>
                              {selectedService.caseStudies?.map((caseStudy, index) => (
                                <motion.div
                                  key={index}
                                  whileHover={{ y: -8, scale: 1.02 }}
                                  transition={{ duration: 0.3 }}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  style={{ height: '100%' }}
                                >
                                  <Box sx={{ 
                                    height: '100%',
                                    p: 0,
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    background: 'white',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                      borderColor: theme.palette.primary.main,
                                      boxShadow: '0 15px 50px rgba(219,29,43,0.15)',
                                    }
                                  }}>
                                    {/* Card Header with gradient */}
                                    <Box sx={{
                                      p: 3,
                                      background: `linear-gradient(135deg, ${index % 3 === 0 ? 'rgba(219,29,43,0.08)' : index % 3 === 1 ? 'rgba(244,114,22,0.08)' : 'rgba(90,125,154,0.08)'} 0%, white 100%)`,
                                      borderBottom: '1px solid rgba(0,0,0,0.04)'
                                    }}>
                                      <Box sx={{ 
                                        display: 'inline-flex',
                                        px: 2,
                                        py: 0.75,
                                        mb: 2,
                                        borderRadius: 2,
                                        bgcolor: index % 3 === 0 ? 'rgba(219,29,43,0.1)' : index % 3 === 1 ? 'rgba(244,114,22,0.1)' : 'rgba(90,125,154,0.1)',
                                      }}>
                                        <Typography variant="caption" sx={{ 
                                          fontWeight: 700, 
                                          color: index % 3 === 0 ? theme.palette.primary.main : index % 3 === 1 ? theme.palette.secondary.main : '#5a7d9a',
                                          textTransform: 'uppercase',
                                          letterSpacing: 1
                                        }}>
                                          Success Story #{index + 1}
                                        </Typography>
                                      </Box>
                                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#231f20', lineHeight: 1.4 }}>
                                        {caseStudy.title}
                                      </Typography>
                                    </Box>
                                    
                                    {/* Card Content */}
                                    <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                      <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#5a6775', flexGrow: 1 }}>
                                        {caseStudy.description}
                                      </Typography>
                                      <Button 
                                        size="small"
                                        endIcon={<span>→</span>}
                                        onClick={() => handleOpenStory(caseStudy)}
                                        sx={{ 
                                          mt: 3,
                                          alignSelf: 'flex-start',
                                          color: theme.palette.primary.main,
                                          fontWeight: 600,
                                          px: 0,
                                          '&:hover': { 
                                            bgcolor: 'transparent',
                                            transform: 'translateX(5px)'
                                          }
                                        }}
                                      >
                                        Read Full Story
                                      </Button>
                                    </Box>
                                  </Box>
                                </motion.div>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </GlassCard>
                    </motion.div>
                    
                    <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button 
                          variant="contained" 
                          size="large"
                          onClick={() => { handleCloseServiceDetails(); scrollToCalculator(); }}
                          sx={{ 
                            background: 'linear-gradient(135deg, #db1d2b 0%, #f47216 100%)',
                            py: 2, 
                            px: 5, 
                            fontWeight: 600,
                          }}
                        >
                          Calculate ROI
                        </Button>
                      </motion.div>
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button 
                          variant="outlined" 
                          size="large"
                          onClick={handleOpenContactDialog}
                          sx={{ 
                            borderColor: '#f47216',
                            borderWidth: 2,
                            color: '#f47216',
                            py: 2, 
                            px: 5,
                            fontWeight: 600,
                            '&:hover': { borderColor: '#db1d2b', borderWidth: 2, color: '#db1d2b' }
                          }}
                        >
                          Contact an Expert
                        </Button>
                      </motion.div>
                    </Box>
                  </Box>
                </Container>
              </Box>
            )}
          </Box>
        </Slide>

        {/* Footer */}
        <Box sx={{ 
          bgcolor: '#231f20', 
          color: 'white', 
          py: 6,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            position: 'absolute', 
            inset: 0, 
            backgroundImage: 'linear-gradient(rgba(244, 114, 22, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(244, 114, 22, 0.03) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={4} alignItems="center" justifyContent="space-between">
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 1, height: '40px' }}>
                    <img src={SopraLogo} alt="Sopra Steria" style={{ height: '100%', width: 'auto' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Automation Factory
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  © {new Date().getFullYear()} Sopra Steria. All rights reserved.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} style={{ display: 'inline-block' }}>
                  <Button
                    variant="contained"
                    onClick={handleOpenContactDialog}
                    sx={{
                      background: 'linear-gradient(135deg, #f47216 0%, #db1d2b 100%)',
                      fontWeight: 600,
                    }}
                  >
                    Get in Touch
                  </Button>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Contact Dialog */}
        <ContactDialog 
          open={contactDialogOpen} 
          onClose={handleCloseContactDialog}
          formData={calculationData}
        />

        {/* Full Story Dialog */}
        <Dialog
          open={Boolean(selectedStory)}
          onClose={handleCloseStory}
          maxWidth="md"
          fullWidth
          scroll="paper"
          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: 'hidden',
              background: '#fafafa',
              boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
            }
          }}
        >
          {selectedStory?.fullStory && (
            <DialogContent sx={{ p: 0 }}>
              {/* Hero header */}
              <Box sx={{
                background: 'linear-gradient(135deg, #db1d2b 0%, #f47216 100%)',
                p: { xs: 3, sm: 4 },
                position: 'relative',
              }}>
                <IconButton
                  onClick={handleCloseStory}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={selectedStory.fullStory.industry}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.75rem' }}
                  />
                  {selectedStory.fullStory.technologies?.map(tech => (
                    <Chip
                      key={tech}
                      label={tech}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, fontSize: '0.75rem' }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  {selectedStory.fullStory.client}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.25, fontSize: { xs: '1.4rem', sm: '1.9rem' } }}>
                  {selectedStory.title}
                </Typography>
              </Box>

              {/* Metrics row */}
              {selectedStory.fullStory.metrics?.length > 0 && (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${selectedStory.fullStory.metrics.length}, 1fr)`,
                  bgcolor: '#231f20',
                }}>
                  {selectedStory.fullStory.metrics.map((m, i) => (
                    <Box key={i} sx={{
                      py: 2.5,
                      px: 2,
                      textAlign: 'center',
                      borderRight: i < selectedStory.fullStory.metrics.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                    }}>
                      <Typography sx={{ color: '#f47216', fontWeight: 800, fontSize: { xs: '1.1rem', sm: '1.4rem' }, fontFamily: '"Outfit", sans-serif', lineHeight: 1 }}>
                        {m.value}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500, display: 'block', mt: 0.5, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        {m.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Body */}
              <Box sx={{ p: { xs: 3, sm: 4 }, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                {/* Challenge */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 4, height: 20, borderRadius: 2, background: 'linear-gradient(180deg, #db1d2b, #f47216)' }} />
                    <Typography variant="overline" sx={{ fontWeight: 700, color: '#db1d2b', letterSpacing: 1.5 }}>
                      The Challenge
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#3a3a3a', lineHeight: 1.85 }}>
                    {selectedStory.fullStory.challenge}
                  </Typography>
                </Box>

                <Divider sx={{ opacity: 0.5 }} />

                {/* Solution */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 4, height: 20, borderRadius: 2, background: 'linear-gradient(180deg, #f47216, #ffa040)' }} />
                    <Typography variant="overline" sx={{ fontWeight: 700, color: '#f47216', letterSpacing: 1.5 }}>
                      Our Solution
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ color: '#3a3a3a', lineHeight: 1.85 }}>
                    {selectedStory.fullStory.solution}
                  </Typography>
                </Box>

                <Divider sx={{ opacity: 0.5 }} />

                {/* Results */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Box sx={{ width: 4, height: 20, borderRadius: 2, background: 'linear-gradient(180deg, #2ecc71, #27ae60)' }} />
                    <Typography variant="overline" sx={{ fontWeight: 700, color: '#27ae60', letterSpacing: 1.5 }}>
                      Results Delivered
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {selectedStory.fullStory.results?.map((result, i) => (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        <Box sx={{
                          mt: 0.35,
                          flexShrink: 0,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          bgcolor: 'rgba(46,204,113,0.12)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                          <Typography sx={{ color: '#27ae60', fontSize: '0.7rem', fontWeight: 800 }}>✓</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#3a3a3a', lineHeight: 1.75, fontWeight: 500 }}>
                          {result}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* CTA */}
                <Box sx={{
                  mt: 1,
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(219,29,43,0.06) 0%, rgba(244,114,22,0.06) 100%)',
                  border: '1px solid rgba(219,29,43,0.12)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#231f20' }}>
                      Ready to achieve similar results?
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5a6775' }}>
                      Use our ROI calculator or get in touch with our team.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => { handleCloseStory(); scrollToCalculator(); }}
                      sx={{
                        background: 'linear-gradient(135deg, #db1d2b 0%, #f47216 100%)',
                        fontWeight: 600,
                        px: 2.5,
                      }}
                    >
                      Calculate ROI
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => { handleCloseStory(); handleOpenContactDialog(); }}
                      sx={{
                        borderColor: '#db1d2b',
                        color: '#db1d2b',
                        fontWeight: 600,
                        px: 2.5,
                        '&:hover': { bgcolor: 'rgba(219,29,43,0.05)' }
                      }}
                    >
                      Contact Us
                    </Button>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
          )}
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default App;
